import { Button, Card, FormLayout, Heading, LoadingDots, Paragraph, Text, useDialog } from '@expressable/syntax-ds';
import { Elements } from '@stripe/react-stripe-js';
import { AppointmentPaymentInput } from 'components/AppointmentPayment/AppointmentPaymentInput';
import { DirectSignupChangePaymentDialog, DirectSignupEditAppointmentDialog } from 'components/Dialog';
import { getStripeFromAccountID } from 'domains/billing';
import { useStripeAccountId } from 'domains/billing/hooks';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { FC, useMemo } from 'react';
import { useController, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatCurrencyDollars } from 'utils/currency';
import { useTrack } from 'utils/hooks/useMixPanel';

type Props = {
  evaluationPrice: number;
};

const Component: FC<Props> = ({ evaluationPrice }) => {
  const { track } = useTrack();
  const {
    filters,
    evaluationSchedule,
    onReturnToFilters,
    onContinueToScheduleEval,
    onSaveIncompleteEvalForm,
    isResumedLead,
  } = useSignupDirectCtx();
  if (!filters || !evaluationSchedule) {
    throw 'Expected filters and evalSelectedTimeSlot to be set';
  }
  const { getValues } = useFormContext<EvaluationFormType>();

  const { dialogStateProps: changePaymentProps, onOpen: onOpenChangePayment } = useDialog();
  const { dialogStateProps: editAppointmentProps } = useDialog();

  const { data } = useStripeAccountId(filters.stateCode);

  const { t } = useTranslation();

  const { field } = useController({
    name: 'billingInfo.stripeMethodId',
  });

  const { isSubmitting } = useFormState();

  const paymentMethod = useWatch<EvaluationFormType, 'billingInfo'>({ name: 'billingInfo' });
  if (!(paymentMethod.type === 'privatePay' || paymentMethod.type === 'insurance-commercial'))
    throw 'Expected payment method to be private pay';

  const stripeElement = useMemo(() => {
    if (!data) return null;
    return getStripeFromAccountID(data.accountId);
  }, [data]);

  if (!data) {
    return (
      <Card className="w-full">
        <LoadingDots message={t('directSignup.eval.payment.loading')} className="bg-transparent static"></LoadingDots>
      </Card>
    );
  }

  if (paymentMethod.type === 'insurance-commercial') return null;

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.evalPayment.title')}
          </Heading>
        </HeadingCombo>
        <Card>
          <Heading level="h6" className="mb-32 uppercase text-[12px]">
            Private Pay
          </Heading>
          <Heading level="h6">{t('reservationCard.appointment.amountDueToday')}</Heading>
          <Text variant="display" className="mb-8">
            {formatCurrencyDollars(evaluationPrice)}
          </Text>
          <Paragraph variant="secondary" size="sm">
            {t('reservationCard.appointment.aboutPricing')}
          </Paragraph>

          {stripeElement && (
            <Elements stripe={stripeElement}>
              <AppointmentPaymentInput
                setPaymentMethod={method => field.onChange(method?.id)}
                rhfName="billingInfo.stripeMethodId"
                label={t('directSignup.eval.payment.labels.creditCard')}
                disabled={isSubmitting}
              />
            </Elements>
          )}
          {paymentMethod.type === 'privatePay' && (
            <>
              <Button
                data-testid="change-payment-method-button"
                variant="link"
                onClick={onOpenChangePayment}
                className="px-0 mt-24"
                size="sm"
              >
                {t('directSignup.sections.evalPayment.changePayment')}
              </Button>
            </>
          )}
        </Card>
      </FormLayout.Fields>
      <DirectSignupChangePaymentDialog
        {...changePaymentProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onReturnToFilters();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_PAYMENT_METHOD.FROM_PRIVATE_PAY, { isResumedLead });
        }}
      />
      <DirectSignupEditAppointmentDialog
        {...editAppointmentProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onContinueToScheduleEval();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_APPOINTMENT, { isResumedLead });
        }}
      />
    </>
  );
};

export const PaymentSection = Component;
